import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { BoxArrowUpRight } from 'react-bootstrap-icons';

import { useNavigation } from '../../../common/navigation';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  GetInvoicePaymentLinkDto,
  GetInvoicePaymentLinkDtoTypeEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import mathUtils from '../../../utils/mathUtils';
import numberFormatter from '../../../common/numberFormatter';
import momencik from '../../../common/momencik';

interface SalesInvoicePaymentLinksProps {
  salesInvoiceId?: string;
  amountGross?: string | null;
  refreshKey?: boolean;
}

const formatDate = (date?: Date | null): string => {
  if (!date) {
    return '—';
  }
  return new Date(date).toLocaleDateString('pl-PL');
};

export const SalesInvoicePaymentLinks: React.FC<
  SalesInvoicePaymentLinksProps
> = ({ salesInvoiceId, amountGross, refreshKey }) => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const [links, setLinks] = useState<GetInvoicePaymentLinkDto[]>([]);

  useEffect(() => {
    if (!salesInvoiceId) {
      return;
    }
    void spd.invoicePayments
      .getLinksByInvoiceId(salesInvoiceId)
      .then(loaded => setLinks(loaded.filter(l => l.active)))
      .catch(console.error);
  }, [salesInvoiceId, spd.invoicePayments, refreshKey]);

  const totalLinked = mathUtils.add(...links.map(l => l.amount || '0'));
  const cap = mathUtils.parse(amountGross ?? '0');
  const remaining = mathUtils.subtract(cap, totalLinked);

  const handleOpenPayment = (invoicePaymentId: string) => {
    nav.invoicePaymentEditor(invoicePaymentId, true);
  };

  return (
    <Table striped hover responsive size="sm">
      <thead>
        <tr>
          <th>Nr sys. wpłaty</th>
          <th>Tytuł</th>
          <th>Data wpłaty</th>
          <th className="amounts-align-right">Kwota wpłaty</th>
          <th className="amounts-align-right">Kwota powiązania</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {links.length === 0 && (
          <tr>
            <td colSpan={6} className="text-center text-muted">
              Brak powiązanych wpłat
            </td>
          </tr>
        )}
        {links.map(link =>
          link.type === GetInvoicePaymentLinkDtoTypeEnum.Correction ? (
            <tr key={link.id}>
              <td colSpan={4} className="fst-italic text-secondary">
                Korekta {link.correctionInvoice?.number ?? '—'} z dnia{' '}
                {momencik(link.correctionInvoice?.invoiceDate)} (nr sys.{' '}
                {link.correctionInvoice?.systemNumber ?? '—'})
                {link.correctionInvoice?.id && (
                  <>
                    {' '}
                    <BoxArrowUpRight
                      className="ms-1 mb-1 icon-clickable icon-primary"
                      title="Otwórz korektę w nowym oknie"
                      onClick={() =>
                        nav.salesInvoiceEditor(link.correctionInvoice?.id, true)
                      }
                    />
                  </>
                )}
              </td>
              <td className="amounts-align-right">
                {numberFormatter(link.amount, true) ?? '—'}
              </td>
              <td></td>
            </tr>
          ) : (
            <tr key={link.id}>
              <td>
                {link.invoicePayment?.systemNumber ?? '—'}{' '}
                <BoxArrowUpRight
                  className="ms-1 mb-1 icon-clickable icon-primary"
                  title="Otwórz wpłatę w nowym oknie"
                  onClick={() => handleOpenPayment(link.invoicePaymentId ?? '')}
                />
              </td>
              <td>{link.invoicePayment?.title ?? '—'}</td>
              <td>{formatDate(link.invoicePayment?.paymentDate)}</td>
              <td className="amounts-align-right">
                {numberFormatter(link.invoicePayment?.amount, true) ?? '—'}
              </td>
              <td className="amounts-align-right">
                {numberFormatter(link.amount, true) ?? '—'}
              </td>
              <td></td>
            </tr>
          ),
        )}
      </tbody>
      {links.length > 0 && (
        <tfoot className="table-group-divider">
          <tr className="fw-semibold">
            <td colSpan={4} className="text-end">
              Kwota do dowiązania:
            </td>
            <td className="amounts-align-right">
              {numberFormatter(amountGross, true)}
            </td>
            <td></td>
          </tr>
          <tr className="fw-semibold">
            <td colSpan={4} className="text-end">
              Łącznie powiązane:
            </td>
            <td className="amounts-align-right">
              {numberFormatter(totalLinked || '0', true)}
            </td>
            <td></td>
          </tr>
          <tr className="fw-semibold table-warning">
            <td colSpan={4} className="text-end">
              Pozostało do dowiązania:
            </td>
            <td className="amounts-align-right">
              {numberFormatter(remaining || '0', true)}
            </td>
            <td></td>
          </tr>
        </tfoot>
      )}
    </Table>
  );
};
